import { create } from 'zustand';

import { PROJECT_INSTANT_VOICE_PRESETS_RELEASE_DATE } from '@/shared/constants/voicePreset';
import { User } from '@/shared/types/User';

import { OnboardingStepKeys } from '../lib/types';

type OnboardingState = {
  currentStep: null | OnboardingStepKeys;
  nextSteps: OnboardingStepKeys[];
  prevSteps: OnboardingStepKeys[];
  stepStatuses: Record<OnboardingStepKeys, boolean>;
};

type OnboardingActions = {
  changeStepStatus: (step: OnboardingStepKeys, status: boolean) => void;
  closeCurrentStep: () => void;
  setNextStep: (step?: OnboardingStepKeys) => void;
  updateOnboardingFromUser: (user: User) => void;
};

const initialState: OnboardingState = {
  currentStep: null,
  nextSteps: [
    'clickToPlay_v1',
    'changeTextSection_v1',
    'audioTrackEditor_v1',
    'customizeVoiceClone_v1',
    'speakerVoicesButton_v1',
    'downloadVideo_v1',
  ],
  prevSteps: [],
  stepStatuses: {
    audioTrackEditor_v1: true,
    changeTextSection_v1: true,
    clickToPlay_v1: true,
    customizeVoiceClone_v1: true,
    downloadVideo_v1: false,
    speakerVoicesButton_v1: true,
  },
};

export const useOnboardingStore = create<OnboardingActions & OnboardingState>((set, get) => ({
  ...initialState,
  changeStepStatus: (step, status) =>
    set((state) => ({
      ...state,
      stepStatuses: {
        ...state.stepStatuses,
        [step]: status,
      },
    })),
  closeCurrentStep: () => {
    const { currentStep } = get();

    if (currentStep) {
      set((state) => ({
        ...state,
        currentStep: null,
        prevSteps: [...state.prevSteps, currentStep],
      }));
    }
  },
  setNextStep: (step) => {
    const { nextSteps, stepStatuses } = get();

    if (step) {
      const stepIndex = nextSteps.indexOf(step);

      if (stepIndex !== -1) {
        set((state) => ({
          ...state,
          currentStep: step,
          nextSteps: state.nextSteps.filter((nextStep) => nextStep !== step),
        }));
      }
    }

    if (!step) {
      const [nextStep, ...steps] = nextSteps;

      if (nextStep && stepStatuses[nextStep]) {
        set((state) => ({
          ...state,
          currentStep: nextStep,
          nextSteps: [...steps],
        }));
      }
    }
  },
  updateOnboardingFromUser: (user) => {
    const completedSteps = user?.onboarding || [];
    const isOldUser = user?.created_date
      ? new Date(user.created_date) < new Date(PROJECT_INSTANT_VOICE_PRESETS_RELEASE_DATE)
      : false;
    const nextSteps = get().nextSteps.filter((step) => !completedSteps.includes(step));

    set((state) => ({
      ...state,
      nextSteps,
      prevSteps: completedSteps,
      stepStatuses: {
        ...state.stepStatuses,
        customizeVoiceClone_v1: !isOldUser,
      },
    }));
  },
}));
